import { useState } from "react";
import { listBranches } from "@/api/locations";
import { useResource } from "@/hooks/useResource";
import { mapEmbedUrl } from "@/lib/links";
import { Button } from "@/components/ui/Button";
import { Container, ErrorState, SectionHeading, SkeletonCards } from "@/components/ui/Primitives";
import { Reveal } from "@/components/motion/Reveal";
import { LocationCard } from "@/components/cards/Cards";
import { Icon } from "@/components/ui/Icon";

/** Find a church: branches from the API, with an on-demand map. */
export function FindChurch() {
  const branches = useResource("branches", (signal) => listBranches(signal));
  const [selected, setSelected] = useState(0);
  const [mapOn, setMapOn] = useState(false);
  const list = branches.data ?? [];
  const current = list[selected] ?? list[0];

  return (
    <section className="find" aria-labelledby="find-title">
      <Container>
        <SectionHeading
          eyebrow="Visit"
          index="06"
          id="find-title"
          title={
            <>
              Find a <em>church</em>
            </>
          }
          lead="Come as you are. There's a seat with your name on it."
          action={
            <Button to="/locations" variant="ghost" iconRight="arrowRight">
              All locations
            </Button>
          }
        />
        {branches.data === undefined && branches.status !== "error" ? <SkeletonCards count={2} variant="text" /> : null}
        {branches.status === "error" && !branches.data ? (
          <div className="find__fallback">
            <ErrorState message={branches.error} onRetry={branches.retry} title="Live location details didn't load" compact />
            <ul className="find__static">
              <li>
                <Icon name="pin" size={16} /> 147 Upper Owina Road, by Evbuotubu Police Station, Benin City
              </li>
              <li>
                <Icon name="pin" size={16} /> 64 Boundary Road, off Airport Road, GRA, Benin City
              </li>
            </ul>
          </div>
        ) : null}
        {list.length ? (
          <div className="find__grid">
            <div className="find__cards">
              {list.slice(0, 4).map((b, i) => (
                <Reveal key={b.id} delay={i * 80}>
                  <LocationCard
                    branch={b}
                    selected={mapOn && i === selected}
                    onSelect={() => {
                      setSelected(i);
                      setMapOn(true);
                    }}
                  />
                </Reveal>
              ))}
            </div>
            <Reveal className="find__map" variant="clip">
              {mapOn && current ? (
                <iframe title={`Map of ${current.name}`} src={mapEmbedUrl(current)} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              ) : (
                <button type="button" className="find__map-facade" onClick={() => setMapOn(true)}>
                  <span className="find__map-grid" aria-hidden />
                  <span className="find__map-pin" aria-hidden>
                    <Icon name="pin" size={26} />
                  </span>
                  <span className="find__map-label">
                    Show {current?.name ?? "the church"} on the map
                    <small>Loads Google Maps</small>
                  </span>
                </button>
              )}
            </Reveal>
          </div>
        ) : branches.status === "success" ? (
          <p className="muted">Location details will be published soon. Email us at ask@miracleassembly.org for directions.</p>
        ) : null}
      </Container>
    </section>
  );
}
